import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Item } from '@react-stately/collections'

import { Drawer } from 'components/Drawer'
import { DrawerProps } from 'components/Drawer/Root'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import { ComboBox } from 'components/ComboBox/ComboBox'
import { FontFamilyPanelBase } from 'components/Design/ShapesTab/components/FontFamilyPanel/FontFamilyPanel'
import { FontRole } from 'types'
import { FontWeightType } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { FONT_WEIGHTS } from 'components/VisualStudioPlayground/Shapes/TextBoxShape/constants'
import { useRootStore } from 'stores/RootStore'
import { useSanitiseFontConfig } from 'components/VisualStudioPlayground/VisualEditor/hooks/useSanitiseFontConfig'

import Styles from './EditDrawer.module.scss'

interface Props extends DrawerProps {
  role: FontRole
  fontFamily: string
  fontWeight: FontWeightType
  fontStyle: string
  onChange: (role: FontRole, family: string, weights: FontWeightType[], style: string) => void
}

const CustomFontWeight = observer(
  ({
    value,
    onChange,
    fontFamily,
  }: {
    value: FontWeightType
    onChange: (weight: FontWeightType) => void
    fontFamily: string
  }) => {
    const { fonts } = useRootStore()
    const font = fonts.get(fontFamily)
    if (!font) return null
    return (
      <ComboBox
        aria-label="Font weight"
        size="md"
        wrapperClassName={Styles.comboboxWrapper}
        contentsClassName={Styles.comboboxContents}
        inputClassName={Styles.comboboxInput}
        inputStyle={{ fontFamily: `'${fontFamily}'`, fontWeight: value }}
        __UNSAFE_popoverStyle={{
          width: 200,
          maxHeight: 300,
          overflow: 'auto',
          zIndex: 10000,
          position: 'fixed',
        }}
        menuTrigger="focus"
        onSelectionChange={(newWeight) => {
          if (!newWeight) return
          onChange(parseInt(newWeight as string, 10) as FontWeightType)
        }}
        selectedKey={value.toString()}
        inputValue={FONT_WEIGHTS[value]}
      >
        {font.weights.map((weight) => {
          const textValue = FONT_WEIGHTS[weight]
          return (
            <Item key={weight.toString()} textValue={textValue}>
              <span style={{ fontWeight: weight, fontFamily: `'${fontFamily}'` }}>{textValue}</span>
            </Item>
          )
        })}
      </ComboBox>
    )
  },
)

const CustomFontFamily = ({
  value,
  onChange,
}: {
  value: string
  onChange: (family: string, weights: FontWeightType[]) => void
}) => {
  const [filter, setFilter] = useState('')

  return (
    <FontFamilyPanelBase
      selectedFont={value}
      filter={filter}
      onFilterChange={setFilter}
      onSelectionChange={(key, weights) => {
        if (!key) return
        onChange(key as string, weights)
      }}
    />
  )
}

export const EditDrawer = observer(({ role, fontFamily, fontWeight, fontStyle, onChange, ...props }: Props) => {
  const [localFontFamily, setLocalFontFamily] = useState(fontFamily)
  const [localFontWeight, setLocalFontWeight] = useState(fontWeight)
  const [localFontStyle, setLocalFontStyle] = useState(fontStyle)
  const sanitiseFontConfig = useSanitiseFontConfig()

  // Initialize local state when props change
  useEffect(() => {
    setLocalFontFamily(fontFamily)
    setLocalFontWeight(fontWeight)
    setLocalFontStyle(fontStyle)
  }, [fontFamily, fontWeight, fontStyle])

  const handleFontFamilyChange = (family: string, weights: FontWeightType[]) => {
    const sanitisedConfig = sanitiseFontConfig({
      fontFamily: family,
      weight: localFontWeight,
      italic: localFontStyle === 'italic',
    })

    setLocalFontFamily(sanitisedConfig.fontFamily)
    setLocalFontWeight(sanitisedConfig.weight)
    setLocalFontStyle(sanitisedConfig.italic ? 'italic' : 'normal')
  }

  const handleSaveChanges = () => {
    onChange(role, localFontFamily, [localFontWeight], localFontStyle)
    // Close the drawer after saving
    if (props.onClose) {
      props.onClose()
    }
  }

  return (
    <Drawer.Root {...props} handleOnly={true}>
      <Drawer.Portal>
        <Drawer.Overlay />
        <Drawer.Content>
          <Drawer.Handle />
          <Drawer.InnerContent>
            <Drawer.Top>
              <Drawer.Close position="left" label="" />
              <Drawer.Title asChild>
                <Heading level={5}>Edit {role === FontRole.Title ? 'Title' : 'Body'} Font</Heading>
              </Drawer.Title>
              <Drawer.Close position="right" />
            </Drawer.Top>
            <Drawer.Container>
              <div className={Styles.fontPicker}>
                <div className={Styles.fontFamilySection}>
                  <CustomFontFamily value={localFontFamily} onChange={handleFontFamilyChange} />
                </div>

                <div className={Styles.fieldGroup}>
                  <CustomFontWeight
                    value={localFontWeight}
                    onChange={setLocalFontWeight}
                    fontFamily={localFontFamily}
                  />
                </div>
              </div>
              <Button size="lg" onPress={handleSaveChanges} fullWidth>
                Save Changes
              </Button>
            </Drawer.Container>
          </Drawer.InnerContent>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
})
